// Single source of truth for dashboard navigation, shared by the Sidebar and
// the Topbar command-search. Keeping it here means "search a tab" and the
// sidebar can never drift apart.
import {
  BookOpen,
  LayoutDashboard,
  FolderKanban,
  ListChecks,
  StickyNote,
  Gauge,
  BarChart3,
  Cpu,
  Users,
  ShieldCheck,
  Activity,
  CreditCard,
  PackageOpen,
  ClipboardList,
  Building2,
  ScrollText,
  LifeBuoy,
  Puzzle,
  Bot,
  Settings,
  KeyRound,
  Bell,
  MailCheck,
  ShieldAlert,
  Gift,
  Sparkles,
  Ticket,
  Megaphone,
  Wrench,
  Receipt,
  Globe,
  Terminal,
  Boxes,
} from "lucide-react";

// "Our Other Products" — the shared themasterdashboard.com feed. Every role gets
// it, so it is built here once rather than copy-pasted into five lists that would
// drift. The path differs per role because each role has its own shell and its own
// route tree: a single flat path would sit outside all of them (and outside their
// auth gates), so each nav link points at the page mounted in that role's own
// shell. The feed itself decides what a viewer may see from their email domain,
// so exposing it to every role reveals nothing role-specific.
const ourProductsNav = (base) => ({
  to: `${base}/our-other-products`,
  label: "Our Other Products",
  icon: Boxes,
  keywords: "other products tools apps suite catalog our more offerings marketplace",
});

// Tester — runs tests, scoped to their company.
const TESTER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, keywords: "home overview start" },
  { to: "/dashboard/generate", label: "Generate Test Cases", icon: Sparkles, keywords: "generate testcases test cases ai create scenarios repo crawl cost" },
  { to: "/dashboard/runs", label: "Runs & Bugs", icon: ListChecks, keywords: "executions history results issues defects findings bugs" },
  { to: "/dashboard/notes", label: "Notes", icon: StickyNote, keywords: "notes memo scratchpad reminders todo write" },
  { to: "/dashboard/extension", label: "Extension Testing", icon: Puzzle, keywords: "extension headed live browser device agent real tab testing" },
  { to: "/dashboard/exe", label: "Exe Agent Testing", icon: Bot, keywords: "exe agent download headed suite machine pc desktop runner always-on testing" },
  // Cloud/headless dashboard testing is paused — the tab stays visible but is
  // disabled (see Sidebar) and its route is hard-blocked (see App.jsx). Placed
  // last, after Exe Agent.
  { to: "/projects", label: "Projects", icon: FolderKanban, keywords: "apps sites crawl", disabled: true, disabledNote: "temporarily disabled" },
  ourProductsNav("/dashboard"),
];

const TESTER_ACCOUNT_NAV = [
  { to: "/dashboard/whats-new", label: "What's New", icon: Sparkles, keywords: "updates changelog release notes latest" },
  { to: "/dashboard/referral", label: "Refer & Earn", icon: Gift, keywords: "referral invite friends reward link share" },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell, keywords: "alerts bell inbox updates messages" },
  // /dashboard/billing is a registered route (App.jsx) that had no nav entry, so
  // the tester plan/invoice page was reachable only from three in-page links on
  // the home dashboard and was invisible to the command palette.
  { to: "/dashboard/billing", label: "Billing & Plan", icon: CreditCard, keywords: "invoice subscription plan upgrade pay coupon usage runs limit" },
  { to: "/dashboard/activity-log", label: "My Activity", icon: ScrollText, keywords: "audit history log events changes what i did" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, keywords: "profile account preferences security 2fa" },
];

// Developer — reviews reports for the projects a tester assigned them (/developer).
const DEVELOPER_NAV = [
  { to: "/developer", label: "Projects", icon: FolderKanban, end: true, keywords: "assigned projects reports test cases bugs fixes" },
  { to: "/developer/build", label: "Build tools", icon: Terminal, keywords: "build rebuild extension exe agent release deploy command terminal venv" },
  ourProductsNav("/developer"),
];

const DEVELOPER_ACCOUNT_NAV = [
  // /developer/whats-new is a registered route (App.jsx) but had no nav entry, so it
  // was unreachable in the UI and invisible to the command palette. Every other role
  // that has the route lists it here.
  { to: "/developer/whats-new", label: "What's New", icon: Sparkles, keywords: "updates changelog release notes latest" },
  { to: "/developer/notifications", label: "Notifications", icon: Bell, keywords: "alerts bell inbox updates messages" },
  { to: "/developer/activity-log", label: "My Activity", icon: ScrollText, keywords: "audit history log events changes what i did" },
  { to: "/developer/settings", label: "Settings", icon: Settings, keywords: "profile account preferences security 2fa" },
];

// Project Manager — plans the work (/pm). They create projects and decide who
// tests and who fixes; they watch everything and pay for nothing (no billing,
// no integrations, no company settings — those stay with the Admin).
const PM_NAV = [
  { to: "/pm", label: "Projects", icon: FolderKanban, end: true, keywords: "projects create assign testers developers staffing apps sites status" },
  { to: "/pm/runs", label: "Runs & Bugs", icon: ListChecks, keywords: "executions history results issues defects findings bugs reports batches" },
  { to: "/pm/team", label: "Team", icon: Users, keywords: "testers developers invite members people staff roster" },
  ourProductsNav("/pm"),
];

const PM_ACCOUNT_NAV = [
  { to: "/pm/notifications", label: "Notifications", icon: Bell, keywords: "alerts bell inbox updates messages" },
  { to: "/pm/activity-log", label: "Activity Log", icon: ScrollText, keywords: "audit history events log changes who did what" },
  // /pm/whats-new is a registered route (App.jsx) but had no nav entry, so a PM
  // could never reach it.
  { to: "/pm/whats-new", label: "What's New", icon: Sparkles, keywords: "updates changelog release notes latest" },
  { to: "/pm/settings", label: "Settings", icon: Settings, keywords: "profile account preferences security 2fa" },
];

// Admin — company owner cockpit (/admin-co).
const COMPANY_ADMIN_NAV = [
  { to: "/admin-co", label: "Overview", icon: Gauge, end: true, keywords: "home dashboard summary" },
  // One tab, not four. Role is a filter chip inside it, so "Project Managers" and
  // "Developers" are each one click rather than a page of their own.
  { to: "/admin-co/team", label: "Team & Tokens", icon: Users, keywords: "users people members roles staff accounts everyone team invite promote demote deactivate remove developers devs engineers project manager pm extension exe agent token key revoke issue seats machines pcs" },
  // /admin-co/projects was a registered route with no nav entry, so the company
  // project list was unreachable in the UI (one deep link aside) and invisible to
  // the command palette.
  { to: "/admin-co/projects", label: "Projects", icon: FolderKanban, keywords: "projects apps sites base url repository crawl create" },
  { to: "/admin-co/activity", label: "Runs & Bugs", icon: ListChecks, keywords: "executions issues" },
  { to: "/admin-co/analytics", label: "Analytics", icon: BarChart3, keywords: "charts metrics insights" },
  { to: "/admin-co/usage-report", label: "Usage Reports", icon: MailCheck, keywords: "email report weekly monthly cost tokens summary usage digest" },
  // WITHDRAWN — Integrations (/admin-co/integrations) and API Keys
  // (/admin-co/keys). Not wanted on the company surface for now. The pages, their
  // API modules and the backend routers are all still in the tree, untouched; the
  // routes are commented out in App.jsx alongside these two lines. To bring
  // either back, restore its entry here AND uncomment its route + import there:
  //
  //   { to: "/admin-co/integrations", label: "Integrations", icon: Webhook,
  //     keywords: "webhooks slack jira connectors notify push events api" },
  //     …and re-add `Webhook` to the lucide-react import at the top of this file.
  //   { to: "/admin-co/keys", label: "API Keys", icon: KeyRound,
  //     keywords: "api keys ci pipeline bot programmatic token integration" },
  ourProductsNav("/admin-co"),
];

const COMPANY_ADMIN_ACCOUNT_NAV = [
  { to: "/admin-co/whats-new", label: "What's New", icon: Sparkles, keywords: "updates changelog release notes latest" },
  { to: "/admin-co/referral", label: "Refer & Earn", icon: Gift, keywords: "referral invite reward link share" },
  { to: "/admin-co/notifications", label: "Notifications", icon: Bell, keywords: "alerts bell inbox updates messages" },
  { to: "/admin-co/billing", label: "Billing & Plan", icon: CreditCard, keywords: "invoice subscription pay coupon" },
  { to: "/admin-co/activity-log", label: "Activity Log", icon: ScrollText, keywords: "audit history events" },
  { to: "/admin-co/settings", label: "Settings", icon: Settings, keywords: "profile account preferences" },
];

// SuperAdmin — platform operator (/admin).
const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: Gauge, end: true, keywords: "home dashboard platform summary" },
  { to: "/admin/companies", label: "Companies", icon: Building2, keywords: "tenants customers accounts" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard, keywords: "plans billing revenue" },
  // /admin/billing was a registered route with no nav entry, so the invoice
  // table was unreachable in the UI and invisible to the command palette.
  { to: "/admin/billing", label: "Billing & Invoices", icon: Receipt, keywords: "invoices payments refunds manual invoice charges receipts" },
  { to: "/admin/plans", label: "Plans", icon: PackageOpen, keywords: "tiers pricing packages" },
  { to: "/admin/country-pricing", label: "Country Pricing", icon: Globe, keywords: "country region price currency geo package" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket, keywords: "discount promo code voucher offer" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, keywords: "charts metrics usage cost" },
  { to: "/admin/usage-report", label: "Usage Report", icon: MailCheck, keywords: "platform email report weekly monthly tenants companies cost digest" },
  { to: "/admin/models", label: "Model Config", icon: Cpu, keywords: "llm agents tuning claude chatgpt openai model" },
  { to: "/admin/qa-context", label: "QA Context", icon: BookOpen, keywords: "qa context business rules vocabulary journeys domain knowledge md file repo" },
  { to: "/admin/guardrails", label: "Guardrails", icon: ShieldCheck, keywords: "budget limits caps spend" },
  { to: "/admin/firewall", label: "AI Firewall", icon: ShieldAlert, keywords: "security pii secrets redact block prompt injection filter safety" },
  { to: "/admin/keys", label: "API Access", icon: KeyRound, keywords: "api keys consumers partners platforms integrations public scopes tokens third party external" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, keywords: "tickets help inbox chat live" },
  { to: "/admin/changelog", label: "Latest Updates", icon: Megaphone, keywords: "changelog whats new release notes publish announcements" },
  { to: "/admin/platform-ops", label: "Platform Ops", icon: Wrench, keywords: "maintenance mode ip blocker block login email inactivity reminder notifications" },
  { to: "/admin/system", label: "System", icon: Activity, keywords: "health status uptime services" },
  { to: "/admin/audit", label: "Audit Log", icon: ClipboardList, keywords: "history events security" },
  ourProductsNav("/admin"),
];

const ADMIN_ACCOUNT_NAV = [
  // Deliberately NO "What's New" viewer here. A SuperAdmin is the author of the
  // changelog, not an audience for it: they manage announcements through
  // "Latest Updates" (/admin/changelog) in the primary nav above, which is where
  // an entry is written, published, targeted and previewed in the list.
  //
  // This entry did exist, added so an author could read back what they had just
  // published. That is the authoring surface's job, and having the consumer feed
  // here as well made the SuperAdmin look like a recipient of their own
  // announcements. The matching route is removed from App.jsx too, so the page
  // is not reachable by typing the URL either — this list is the navigation, not
  // the access control.
  { to: "/admin/notifications", label: "Notifications", icon: Bell, keywords: "alerts bell inbox updates messages" },
  // The only entry here that isn't under /admin — the shared standalone
  // enrolment page, so 2FA is one click away rather than buried in a tab.
  // Optional, like it is for every role.
  { to: "/security/2fa", label: "Two-Factor Auth", icon: ShieldCheck, keywords: "2fa totp mfa authenticator google authy backup codes security" },
  { to: "/admin/settings", label: "Settings", icon: Settings, keywords: "profile account preferences" },
];

export const NAV_BY_ROLE = {
  tester: { primary: TESTER_NAV, account: TESTER_ACCOUNT_NAV, section: "Workspace" },
  project_manager: { primary: PM_NAV, account: PM_ACCOUNT_NAV, section: "Project" },
  developer: { primary: DEVELOPER_NAV, account: DEVELOPER_ACCOUNT_NAV, section: "Developer" },
  admin: { primary: COMPANY_ADMIN_NAV, account: COMPANY_ADMIN_ACCOUNT_NAV, section: "Company" },
  superadmin: { primary: ADMIN_NAV, account: ADMIN_ACCOUNT_NAV, section: "Platform" },
};

export const ROLE_LABEL = {
  tester: "Tester", developer: "Developer", project_manager: "Project Manager",
  admin: "Admin", superadmin: "SuperAdmin",
};
export const HOME_BY_ROLE = {
  tester: "/dashboard", developer: "/developer", project_manager: "/pm",
  admin: "/admin-co", superadmin: "/admin",
};
export const NOTIF_PATH = {
  tester: "/dashboard/notifications",
  developer: "/developer/notifications",
  project_manager: "/pm/notifications",
  admin: "/admin-co/notifications",
  superadmin: "/admin/notifications",
};
// Where "my account" goes, per role. The Sidebar used to infer this from
// account-nav[0], which is Notifications for a developer and What's New for a
// tester — so the user card labelled "Account settings" opened the wrong page for
// every role, and disagreed with the topbar icon sitting inches away.
export const SETTINGS_PATH = {
  tester: "/dashboard/settings",
  developer: "/developer/settings",
  project_manager: "/pm/settings",
  admin: "/admin-co/settings",
  superadmin: "/admin/settings",
};

// An unrecognised role gets NOTHING, not the tester surface. Defaulting an
// unknown role to a working dashboard is how a role typo turns into an access
// grant — the caller decides what to show when this comes back empty.
export const EMPTY_NAV = { primary: [], account: [], section: "" };

export function navForRole(role) {
  return NAV_BY_ROLE[role] || EMPTY_NAV;
}

// Flat list of every navigable destination for a role, tagged with its group.
// Disabled items (e.g. the paused Projects tab) are excluded so they can't be
// reached via the Topbar command palette either.
export function searchableNav(role) {
  const nav = navForRole(role);
  return [
    ...nav.primary.filter((i) => !i.disabled).map((i) => ({ ...i, group: nav.section })),
    ...nav.account.filter((i) => !i.disabled).map((i) => ({ ...i, group: "Account" })),
  ];
}

// Resolve the active nav item for a pathname → used to render the breadcrumb.
// Picks the longest matching `to` so "/admin/companies" beats "/admin".
export function matchNavItem(role, pathname) {
  const nav = navForRole(role);
  const all = [...nav.primary, ...nav.account];
  let best = null;
  let prefixBest = null;
  for (const item of all) {
    const isPrefix = pathname === item.to || pathname.startsWith(item.to + "/");
    const isMatch = item.end ? pathname === item.to : isPrefix;
    if (isMatch && (!best || item.to.length > best.to.length)) best = item;
    // Remember the longest prefix match separately, ignoring `end`.
    if (isPrefix && (!prefixBest || item.to.length > prefixBest.to.length)) prefixBest = item;
  }
  // An `end: true` item owns exactly one path, so a deep sub-route under it matched
  // nothing at all and the breadcrumb rendered "—" with no sidebar item highlighted.
  // That is every detail page of the index-route dashboards: /developer/projects/:id,
  // /developer/projects/:id/:kind/:batchId, /pm/projects/:id, and so on.
  return { section: nav.section, item: best || prefixBest };
}
